import React, { ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import { Button } from '../../../../../ui';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import {
  createAssessmentSettings,
  getAssessmentSettings,
  updateAssessmentSettings,
} from '../../../../../utils/api_service/assessment_api';
import { Permission } from '../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../utils/hooks/PermissionGate';
import usePermissions from '../../../../../utils/hooks/usePermissions';
import {
  AssessmentSettings,
  AssessmentType,
} from '../../../../../utils/types/assessment_types';

interface ISettingsProps {
  assessmentType: AssessmentType;
  assessmentId?: string;
  skipSpecificSettings?: boolean;
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
}

interface SettingRowProps {
  label: string;
  description: string;
  children: React.ReactNode;
  isTextArea?: boolean;
}

const SettingRow = ({
  label,
  description,
  children,
  isTextArea,
}: SettingRowProps) => (
  <div
      className={`flex flex-col ${
        isTextArea ? 'gap-4' : 'sm:flex-row sm:items-center justify-between gap-4'
      } py-2.5 hover:bg-slate-50/50 transition-colors`}
  >
    <div className='min-w-0'>
      <h3 className='mb-0.5 text-[13px] font-bold text-slate-800'>{ label }</h3>
      <p className='text-[11px] leading-snug text-slate-500'>{ description }</p>
    </div>
    <div className={isTextArea ? 'w-full' : 'shrink-0'}>{ children }</div>
  </div>
);

export const Settings = ({
  assessmentType,
  assessmentId,
  skipSpecificSettings,
  setActiveTab,
}: ISettingsProps) => {
  const { id } = useParams<{ id: string }>();
  const { hasPermissions } = usePermissions();
  const [settingsExist, setSettingsExist] = useState<boolean>(false);
  const [settings, setSettings] = useState<AssessmentSettings>({
    assessmentId: assessmentId ?? id,
    showScore: false,
    customMessage: '',
    shuffleQuestions: false,
    enableCopy: false,
    attemptCount: '1',
    blurVideo: false,
    unsuccessfulEmail: false,
  });

  useEffect(() => {
    getAssessmentSettings(assessmentId ?? id).then(
      (response: AssessmentSettings) => {
        if (response.assessmentId) {
          setSettingsExist(true);
          setSettings({
            ...response,
            attemptCount: response.attemptCount.toString(),
          });
        }
      },
    );
  }, []);

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSettings({
      ...settings,
      [event.target.name]: event.target.value,
    });
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = settingsExist
        ? await updateAssessmentSettings(assessmentId ?? id, settings)
        : await createAssessmentSettings(settings);

      if (response.ok) {
        toast.success(
          `Assessment Settings ${
            settingsExist ? 'Updated' : 'Created'
          } Successfully!`,
          {
            position: 'bottom-center',
          },
        );
      }
    } catch (error) {
      toast.error('Failed to save settings. Please try again.', {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
      setActiveTab('invitationTemplate');
    }
  };

  const canEdit = hasPermissions([Permission.ASSESSMENTS_EDIT]);

  const toggleSetting = (key: keyof AssessmentSettings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const mainSettings = [
    {
      key: 'showScore' as keyof AssessmentSettings,
      label: 'Score Display',
      description:
        'Allow candidates to view their total score immediately after finishing the assessment.',
      show: !skipSpecificSettings,
    },
    {
      key: 'shuffleQuestions' as keyof AssessmentSettings,
      label: 'Shuffle Questions',
      description:
        'Randomize the order of questions for each candidate to prevent collaboration and cheating.',
      show: true,
    },
    {
      key: 'enableCopy' as keyof AssessmentSettings,
      label: 'Copy & Paste Protection',
      description:
        'Restrict clipboard operations (copy, paste, cut) within the assessment environment.',
      show: true,
    },
    {
      key: 'blurVideo' as keyof AssessmentSettings,
      label: 'Video Privacy',
      description:
        'Enable background blur or face blurring to protect candidate anonymity during the session.',
      show: true,
    },
  ];

  return (
    <div className='overflow-hidden bg-white shadow-sm'>
      <div className='border-b border-slate-100 bg-slate-50/50 pb-2'>
        <h2 className='text-sm font-bold text-slate-800'>
          General Configuration
        </h2>
      </div>

      <div className='divide-y divide-slate-100'>
        { mainSettings
          .filter((s) => s.show)
          .map((setting) => (
            <SettingRow
                key={setting.key}
                label={setting.label}
                description={setting.description}
            >
              <ToggleInput
                  toggled={!!settings[setting.key]}
                  handleToggle={() => toggleSetting(setting.key)}
                  name={setting.key}
                  informationText={`Enable ${setting.label.toLowerCase()}`}
                  disabled={!canEdit}
              />
            </SettingRow>
          )) }

        { !skipSpecificSettings && (
          <SettingRow
              label='Attempt Limit'
              description='Specify the maximum number of times a candidate can attempt this assessment.'
          >
            <div className='w-full sm:w-[140px]'>
              <TextInput
                  name='attemptCount'
                  value={settings.attemptCount}
                  type='number'
                  onChange={handleInputChange}
                  min='1'
                  max='10'
                  disabled={!canEdit}
              />
            </div>
          </SettingRow>
        ) }

        { assessmentType === 'candidate' && !skipSpecificSettings && (
          <>
            <SettingRow
                label='Rejection Automation'
                description='Automatically send a standardized rejection email to unsuccessful candidates.'
            >
              <ToggleInput
                  toggled={settings.unsuccessfulEmail ?? false}
                  handleToggle={() => toggleSetting('unsuccessfulEmail')}
                  name='unsuccessfulEmail'
                  informationText='Auto-notify'
                  disabled={!canEdit}
              />
            </SettingRow>

            <SettingRow
                label='Success Threshold'
                description='The minimum percentage score required for a candidate to be considered successful.'
            >
              <div className='w-full sm:w-[140px]'>
                <TextInput
                    name='thresholdPercent'
                    value={settings.thresholdPercent ?? '0'}
                    type='number'
                    onChange={handleInputChange}
                    min='0'
                    max='100'
                    disabled={!canEdit}
                />
              </div>
            </SettingRow>
          </>
        ) }

        { !skipSpecificSettings && (
          <SettingRow
              label='Custom Completion Message'
              description='This message will be displayed to candidates once they submit their assessment.'
              isTextArea
          >
            <TextArea
                name='customMessage'
                value={settings.customMessage}
                onChange={handleInputChange}
                disabled={!canEdit}
            />
          </SettingRow>
        ) }
      </div>

      <div className='flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 p-3'>
        <p className='text-[11px] text-slate-400 font-medium italic hidden sm:block'>
          * Ensure all changes are verified before applying them to the live
          assessment.
        </p>
        <PermissionGate requiredPermissions={[Permission.ASSESSMENTS_EDIT]}>
          <Button
              onClick={handleSubmit}
              variant='primary'
              label={isLoading ? 'Updating...' : 'Apply Changes'}
              disabled={isLoading}
              className='!h-8 !px-5 !rounded-lg text-xs font-bold shadow-sm'
          />
        </PermissionGate>
      </div>
    </div>
  );
};
